import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FinanceV2Layout from "./FinanceV2Layout";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import "./FinanceV2.css";

const STEPS = [
  { id: 1, title: "Company & books start date" },
  { id: 2, title: "Chart of accounts" },
  { id: 3, title: "Opening balances" },
  { id: 4, title: "Payment methods" },
  { id: 5, title: "Revenue mapping" },
  { id: 6, title: "Expense categories" },
  { id: 7, title: "Finish" },
];

export default function FinanceV2SetupWizard() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [wizardOptions, setWizardOptions] = useState(null);
  const [company, setCompany] = useState({
    company_name: "Vostro World",
    currency: "PKR",
    fiscal_year_start_month: 1,
    books_start_date: new Date().toISOString().split("T")[0],
  });
  const [balances, setBalances] = useState([]);
  const [paymentMappings, setPaymentMappings] = useState([]);
  const [revenueMappings, setRevenueMappings] = useState([]);
  const [expenseMappings, setExpenseMappings] = useState([]);

  useEffect(() => {
    loadOptions();
  }, [branch_id]);

  const loadOptions = async () => {
    try {
      const [optRes, accRes] = await Promise.all([
        FinanceV2Services.getWizardOptions(branch_id),
        FinanceV2Services.getAccountsList(branch_id),
      ]);
      setWizardOptions(optRes.data?.data);
      const acc = accRes.data?.data || [];
      setAccounts(acc);
      setBalances(
        acc
          .filter((a) => ["asset", "liability", "equity"].includes(a.account_type?.code))
          .map((a) => ({
            account_id: a.id,
            code: a.code,
            name: a.name,
            debit: "",
            credit: "",
          }))
      );
      if (optRes.data?.data?.payment_methods) {
        setPaymentMappings(
          optRes.data.data.payment_methods.map((pm) => ({
            source_module: "sales",
            source_type: "payment_method",
            source_key: pm.id,
            source_label: pm.name,
            account_id: "",
          }))
        );
      }
      if (optRes.data?.data?.package_categories) {
        setRevenueMappings(
          optRes.data.data.package_categories.map((c) => ({
            source_module: "sales",
            source_type: "package_category",
            source_key: c.id,
            source_label: c.name,
            account_id: "",
          }))
        );
      }
      if (optRes.data?.data?.legacy_expense_categories) {
        setExpenseMappings(
          optRes.data.data.legacy_expense_categories.map((c) => ({
            source_module: "legacy_expense",
            source_type: "expense_category",
            source_key: c.id,
            source_label: c.name,
            account_id: "",
          }))
        );
      }
    } catch (e) {
      toast.error("Failed to load setup options");
    }
  };

  const saveCompany = async () => {
    setLoading(true);
    try {
      await FinanceV2Services.saveCompany({ ...company, branch_id });
      toast.success("Company settings saved");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const seedChart = async () => {
    setLoading(true);
    try {
      await FinanceV2Services.seedChart(branch_id);
      toast.success("QuickBooks-style chart of accounts created");
      const accRes = await FinanceV2Services.getAccountsList(branch_id);
      const acc = accRes.data?.data || [];
      setAccounts(acc);
      setBalances(
        acc
          .filter((a) => ["asset", "liability", "equity"].includes(a.account_type?.code))
          .map((a) => ({
            account_id: a.id,
            code: a.code,
            name: a.name,
            debit: "",
            credit: "",
          }))
      );
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.message || "Seed failed");
    } finally {
      setLoading(false);
    }
  };

  const saveOpeningBalances = async () => {
    const payload = balances
      .filter((b) => parseFloat(b.debit) > 0 || parseFloat(b.credit) > 0)
      .map((b) => ({
        account_id: b.account_id,
        debit: parseFloat(b.debit) || 0,
        credit: parseFloat(b.credit) || 0,
      }));
    if (!payload.length) {
      toast.info("No opening balances entered — you can add later via journal");
      setStep(4);
      return;
    }
    setLoading(true);
    try {
      await FinanceV2Services.saveOpeningBalances({
        as_of_date: company.books_start_date,
        balances: payload,
        branch_id,
      });
      toast.success("Opening balances posted (double-entry)");
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.message || "Opening balances failed");
    } finally {
      setLoading(false);
    }
  };

  const savePaymentMappings = async () => {
    const mappings = paymentMappings.filter((m) => m.account_id);
    if (mappings.length) {
      await FinanceV2Services.saveMappings({ mappings, branch_id });
    }
    setStep(5);
  };

  const saveRevenueMappings = async () => {
    setLoading(true);
    try {
      await FinanceV2Services.seedMappingDefaults(branch_id);
      const mappings = revenueMappings.filter((m) => m.account_id);
      if (mappings.length) {
        await FinanceV2Services.saveMappings({ mappings, branch_id });
      }
      setStep(6);
    } catch (e) {
      toast.error("Revenue mapping failed");
    } finally {
      setLoading(false);
    }
  };

  const saveExpenseMappings = async () => {
    setLoading(true);
    try {
      const mappings = expenseMappings.filter((m) => m.account_id);
      if (mappings.length) {
        await FinanceV2Services.saveMappings({ mappings, branch_id });
      }
      setStep(7);
    } catch (e) {
      toast.error("Expense mapping failed");
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    setLoading(true);
    try {
      await FinanceV2Services.completeSetup(branch_id);
      toast.success("Finance V2 is ready. New sales will sync automatically.");
      navigate("/finance-v2/dashboard");
    } catch (e) {
      toast.error("Could not complete setup");
    } finally {
      setLoading(false);
    }
  };

  const incomeAccounts = accounts.filter((a) => a.account_type?.code === "income");
  const expenseAccounts = accounts.filter((a) => a.account_type?.code === "expense");

  return (
    <FinanceV2Layout>
      <div className="container-fluid px-0 py-2">
        <h4 className="mb-1">Finance V2 — First-time setup</h4>
        <p className="text-muted">
          QuickBooks-style double-entry books. After launch, enter all new finance
          activity in Finance V2 — legacy screens are for history only.
          Enter opening cash, bank, and balances once — then sales post automatically from POS.
        </p>

        <div className="fv2-step-nav mb-3">
          {STEPS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`btn btn-sm ${step === s.id ? "btn-success" : "btn-outline-secondary"}`}
              onClick={() => setStep(s.id)}
            >
              {s.id}. {s.title}
            </button>
          ))}
        </div>

        <div className="fv2-card">
          {step === 1 && (
            <>
              <h5>Company & books start date</h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Company name</label>
                  <input
                    className="form-control"
                    value={company.company_name}
                    onChange={(e) => setCompany({ ...company, company_name: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Currency</label>
                  <input
                    className="form-control"
                    value={company.currency}
                    onChange={(e) => setCompany({ ...company, currency: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Fiscal year starts (month)</label>
                  <input
                    type="number"
                    min={1}
                    max={12}
                    className="form-control"
                    value={company.fiscal_year_start_month}
                    onChange={(e) =>
                      setCompany({ ...company, fiscal_year_start_month: +e.target.value })
                    }
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Books start date (go-live)</label>
                  <input
                    type="date"
                    className="form-control"
                    value={company.books_start_date}
                    onChange={(e) =>
                      setCompany({ ...company, books_start_date: e.target.value })
                    }
                  />
                </div>
              </div>
              <button className="btn btn-success mt-3" disabled={loading} onClick={saveCompany}>
                Save & continue
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h5>Chart of accounts</h5>
              <p className="text-muted">
                Loads standard accounts: Cash, Bank, AR, AP, Sales Tax (FBR), Revenue by service, Expenses, Opening Balance Equity.
              </p>
              <button className="btn btn-success" disabled={loading} onClick={seedChart}>
                Create chart of accounts
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h5>Opening balances (as of {company.books_start_date})</h5>
              <p className="text-muted">
                Ask your finance manager: cash in hand, each bank balance, petty cash, loans, etc.
                System balances via Opening Balance Equity (QuickBooks standard).
              </p>
              <div className="fv2-table-wrap">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Account</th>
                      <th>Debit</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balances.map((b, i) => (
                      <tr key={b.account_id}>
                        <td>{b.code}</td>
                        <td>{b.name}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={b.debit}
                            onChange={(e) => {
                              const next = [...balances];
                              next[i].debit = e.target.value;
                              setBalances(next);
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={b.credit}
                            onChange={(e) => {
                              const next = [...balances];
                              next[i].credit = e.target.value;
                              setBalances(next);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="btn btn-success mt-2" disabled={loading} onClick={saveOpeningBalances}>
                Post opening balances
              </button>
            </>
          )}

          {step === 4 && (
            <>
              <h5>Map payment methods → cash/bank accounts</h5>
              <p className="text-muted">Links checkout payment types to the correct asset account.</p>
              {paymentMappings.map((m, i) => (
                <div className="row g-2 mb-2 align-items-center" key={m.source_key}>
                  <div className="col-md-4">{m.source_label}</div>
                  <div className="col-md-6">
                    <select
                      className="form-select form-select-sm"
                      value={m.account_id}
                      onChange={(e) => {
                        const next = [...paymentMappings];
                        next[i].account_id = e.target.value;
                        setPaymentMappings(next);
                      }}
                    >
                      <option value="">— Select account —</option>
                      {accounts
                        .filter((a) => a.account_type?.code === "asset")
                        .map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} — {a.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              ))}
              <button className="btn btn-success mt-2" onClick={savePaymentMappings}>
                Save & continue
              </button>
            </>
          )}

          {step === 5 && (
            <>
              <h5>Map package types → revenue accounts</h5>
              <p className="text-muted">
                Gym, PT, Cafe, Nutrition, etc. map to income accounts for P&amp;L by service.
              </p>
              {revenueMappings.map((m, i) => (
                <div className="row g-2 mb-2 align-items-center" key={m.source_key}>
                  <div className="col-md-4">{m.source_label}</div>
                  <div className="col-md-6">
                    <select
                      className="form-select form-select-sm"
                      value={m.account_id}
                      onChange={(e) => {
                        const next = [...revenueMappings];
                        next[i].account_id = e.target.value;
                        setRevenueMappings(next);
                      }}
                    >
                      <option value="">— Default / auto —</option>
                      {incomeAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              <button className="btn btn-success mt-2" disabled={loading} onClick={saveRevenueMappings}>
                Save & continue
              </button>
            </>
          )}

          {step === 6 && (
            <>
              <h5>Map legacy expense categories → expense accounts</h5>
              <p className="text-muted">
                Used when importing old expenses and vostro expenses into Finance V2.
              </p>
              {expenseMappings.map((m, i) => (
                <div className="row g-2 mb-2 align-items-center" key={m.source_key}>
                  <div className="col-md-4">{m.source_label}</div>
                  <div className="col-md-6">
                    <select
                      className="form-select form-select-sm"
                      value={m.account_id}
                      onChange={(e) => {
                        const next = [...expenseMappings];
                        next[i].account_id = e.target.value;
                        setExpenseMappings(next);
                      }}
                    >
                      <option value="">— Default expense —</option>
                      {expenseAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              <button className="btn btn-success mt-2" disabled={loading} onClick={saveExpenseMappings}>
                Save & continue
              </button>
            </>
          )}

          {step === 7 && (
            <>
              <h5>Complete setup</h5>
              <ul>
                <li>New sales (orders) will post automatic journal entries</li>
                <li>FBR invoice numbers stay on orders — shown on journal description</li>
                <li>Enter all new expenses / transfers / charity in Finance V2 → Enter transactions</li>
                <li>Import historical data once from Import center — do not keep using legacy Add screens</li>
              </ul>
              <button className="btn btn-success btn-lg" disabled={loading} onClick={finish}>
                Launch Finance V2
              </button>
            </>
          )}
        </div>
      </div>
    </FinanceV2Layout>
  );
}
